import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { Dropdown, PERSISTED_DEALER_ID, PERSISTED_LOCATION_ID, persistItem } from "components";
import {
  setAllLocationsSelectedDealerId,
  setIsAllLocationsSelected,
  setSelectedDealer,
  setSelectedLocation,
  useAllLocationsSelectedDealerId,
  useDealerAndLocationsGroupedOptions,
  useIsAllLocationsSelected,
  usePermanentLocations,
  usePermanentRealtimeDealers,
  useSelectedDealer,
  useSelectedLocation
} from "components/DealersAndLocationsDropdown/hooks";
import "components/LocationDropdown/LocationDropdown.scss";
import { LOCATION_DROPDOWN_STORAGE_KEYS } from "components/LocationDropdown/storageKeys";
import { usePermanentRealTimeUser } from "hooks";
import { setPreference } from "utils/common";

import type { DropdownItemProps } from "components";
import type { ReactElement, SyntheticEvent } from "react";
import type { DropdownOnSearchChangeData } from "semantic-ui-react";

export const LocationDropdown = (): ReactElement => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const { dealers } = usePermanentRealtimeDealers();
  const [showDropdown, setShowDropdown] = useState(false);
  const { user } = usePermanentRealTimeUser();
  const { locations } = usePermanentLocations();
  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();
  const dealerAndLocationsGroupedOptions = useDealerAndLocationsGroupedOptions({ searchTerm, selectAll: true });
  const { isAllLocationsSelected } = useIsAllLocationsSelected();
  const { allLocationsSelectedDealerId } = useAllLocationsSelectedDealerId();

  const getPlaceholderName = () => {
    if (isAllLocationsSelected) return t("v8_all_locations", "All Locations");
    if (selectedLocation && selectedDealer) return `${selectedDealer.name} - ${selectedLocation.name}`;

    return t("v8_select_location", "Select a location");
  };

  const handleOnLocationChange = (_: SyntheticEvent, data: DropdownItemProps<string>) => {
    const value = parseInt(data.value.split("-").at(1) ?? "0");

    setIsAllLocationsSelected(data.key === "all");
    setAllLocationsSelectedDealerId(data.key === "all" ? value : 0);

    const location_id = data.key === "all" ? (user?.dealer_location_id ?? 0) : value;
    const location = locations.find(loc => loc.id === location_id);
    if (!location) return;

    const dealerId = data.key === "all" ? parseInt(data.value.split("-").at(1) ?? "0") : location.dealer_id;
    const dealer = dealers.find(d => dealerId === d.id);
    if (!dealer) return;

    persistItem(PERSISTED_LOCATION_ID, location_id.toString());
    persistItem(PERSISTED_DEALER_ID, dealerId.toString());

    setSelectedDealer(dealer);
    setSelectedLocation(location);
  };

  const handleSearch = (_: SyntheticEvent, data: DropdownOnSearchChangeData) => {
    setSearchTerm(data.searchQuery);
  };

  const handleDropdownItemClick = (e: SyntheticEvent<Element, Event>, option: DropdownItemProps<string>) => {
    e.stopPropagation();
    handleOnLocationChange(e, option);
    setSearchTerm("");
    setShowDropdown(false);
    setPreference(LOCATION_DROPDOWN_STORAGE_KEYS.AllLocationsSelectedPreference, option.key === "all");
  };

  const handleCloseDropdown = () => {
    setSearchTerm("");
    setShowDropdown(false);
  };

  const getActiveOption = (key: string, value: string) => {
    if (!isAllLocationsSelected) return selectedLocation?.id === value.split("-").at(1);

    return key === "all" && value.split("-").at(1) === allLocationsSelectedDealerId.toString();
  };

  return (
    <div className="DealerLocationDropdown-wrapper">
      <Dropdown
        button
        className={`LocationDropdown icon -scrollable-dropdown nav-bar-map`}
        floating
        onClick={() => setShowDropdown(true)}
        labeled
        icon={
          <div className="location-placeholder-icon">
            <Icon className="location dot regular" />
          </div>
        }
        noResultsMessage={t("v8_no_locations", "No locations")}
        placeholder={getPlaceholderName()}
        renderLabel={(option: DropdownItemProps) => ({ content: option.labelText, icon: option.icon })}
        upward={false}
        fluid={false}
        scrolling
        searchQuery={searchTerm}
        search
        open={showDropdown}
        onClose={handleCloseDropdown}
        onSearchChange={handleSearch}
      >
        <DropdownMenu className="location-dropdown-menu">
          {dealerAndLocationsGroupedOptions.map(option => (
            <DropdownItem
              onClick={e => handleDropdownItemClick(e, option)}
              active={getActiveOption(option.key.toString(), option.value)}
              key={option.key}
              disabled={option.disabled}
              className={`locationItem ${option.isLastLocation ? "last-location-item" : ""}`}
            >
              <Icon className={option.icon} />
              <span className={option.icon === "house" ? "dealer-dropdown-option" : ""}>{option.text}</span>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};
